/**
 * Profile Picker — W10-A
 *
 * Displayed at boot time when multiple profiles exist so the correct
 * commander is active before entering the home screen.
 *
 * Also used after "切換指揮官" from Settings.
 */

import {
  listProfiles,
  switchProfile,
  createProfile,
  ensureFamilyState,
} from '../shared/profile';
import { loadProgress, createFreshProgress, saveProgress } from '../shared/progress';
import type { ProfileMeta } from '../shared/types';

export interface ProfilePickerOptions {
  /** Called when a profile has been selected and is now active. */
  onPicked: () => void;
}

export function renderProfilePicker(
  container: HTMLElement,
  options: ProfilePickerOptions,
): void {
  ensureFamilyState();
  const profiles = listProfiles();
  _render(container, profiles, options);
}

function _render(
  container: HTMLElement,
  profiles: ProfileMeta[],
  options: ProfilePickerOptions,
): void {
  const cards = profiles
    .map(
      (p) => `
      <button
        class="profile-card"
        data-profile-id="${p.id}"
        style="border-color: ${p.avatarColor ?? '#4FC3F7'};"
        aria-label="選擇指揮官 ${p.nickname}"
      >
        <div
          class="profile-avatar"
          style="background: ${p.avatarColor ?? '#4FC3F7'};"
        >
          ${p.nickname.charAt(0).toUpperCase()}
        </div>
        <span class="profile-nickname">${p.nickname}</span>
      </button>`,
    )
    .join('');

  container.innerHTML = `
    <div class="profile-picker-screen">
      <h1>🏰 永恆要塞</h1>
      <h2>選擇指揮官</h2>
      <div class="profile-list">
        ${cards}
      </div>
      <button class="secondary-btn" id="add-profile-btn">+ 新增指揮官</button>
    </div>
  `;

  // Wire card clicks
  container.querySelectorAll('.profile-card').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset['profileId']!;
      switchProfile(id);

      // If the profile has no progress yet, create it now
      const progress = loadProgress(id);
      if (!progress) {
        const meta = listProfiles().find((p) => p.id === id);
        const fresh = createFreshProgress(meta?.nickname ?? '指揮官', id);
        saveProgress(fresh, id);
      }

      options.onPicked();
    });
  });

  // Add-profile button
  (container.querySelector('#add-profile-btn') as HTMLButtonElement).addEventListener(
    'click',
    () => {
      const nickname = prompt('新指揮官的名字:')?.trim();
      if (!nickname) return;
      const newProfile = createProfile(nickname);
      // Create fresh progress for the new profile immediately
      const fresh = createFreshProgress(nickname, newProfile.id);
      saveProgress(fresh, newProfile.id);
      // Switch to new profile and proceed
      switchProfile(newProfile.id);
      options.onPicked();
    },
  );
}
